import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import {
  buildResponseValidationReport,
  isAbortError,
  type KyRequestOptions,
} from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { type UserIdentityProviderLinksAdmin } from '../types';
import { isExpectedUserIdentityProviderLinkError } from '../userIdentityProviderLinkError';
import { zodUserIdentityProviderLinksAdminResponseSchema } from '../validations';

/**
 * Fetches the single sign-on links of a user's login as ADMIN.
 *
 * The response is parsed strictly: a body that does not match throws, so callers can read
 * the list unguarded. Errors are rethrown without a toast. The error code `login_not_found`
 * means the user has no login, an expected answer that is not logged.
 *
 * @param userId - The ID of the user.
 * @param options - Additional request options, e.g. an abort signal.
 * @returns The user's links, one per identity provider.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-User/operation/list_identity_provider_links_vadmin_users__user_id__identity_provider_links_get
 */
export const fetchUserIdentityProviderLinksAdmin = async (
  userId: UUID,
  options?: KyRequestOptions,
): Promise<UserIdentityProviderLinksAdmin> => {
  let response: unknown;

  try {
    response = await vestigasApi
      .get(ENDPOINT.USER.GET_IDENTITY_PROVIDER_LINKS_ADMIN(userId), options)
      .json();
  } catch (error) {
    if (
      !isAbortError(error) &&
      !isExpectedUserIdentityProviderLinkError(error)
    ) {
      log.error({
        message: 'Error fetching user identity provider links',
        error,
      });
    }

    throw error;
  }

  const parsed =
    zodUserIdentityProviderLinksAdminResponseSchema.safeParse(response);

  if (!parsed.success) {
    log.responseValidation({
      ...buildResponseValidationReport(parsed.error.issues),
      key: 'user-identity-provider-links-admin',
      message: 'user identity provider links response has validation errors',
    });

    throw new Error(
      'User identity provider links response did not match the expected shape',
    );
  }

  return parsed.data;
};
